// The identity picture choice of the MAP section (ui-map-interface.md
// §2.5, R3298, R3316): DARK and LIGHT as two preview tiles, as the chart
// reading shows its three (`chartpick.ts`).
//
// Each picture draws a small piece of the chrome in the inks of its own
// identity: the deep water, a panel with a title and two lines of text,
// and an accent press. The inks are the dark body and the light body of
// one faction theme (`LIGHT_CHROME_FACTIONS` of `gen/inks.ts`), so a tile
// reads its identity on a dark page and on a light page alike. The
// picture holds no word of a language, and the SVG is `aria-hidden`.

import { LIGHT_CHROME_FACTIONS, type ChromeBody } from '../../gen/inks';
import { IDENTITIES, type Identity } from '../../identity/pick';
import { choice as CS } from '../../gen/strings';
import { confirmStrip, previewTiles, type Confirm } from '../choice/choice';
import { FALLBACK_FACTION } from '../theme';

/** The dark body and the light body of faction `id`; an id outside the
 *  roster takes the base faction. */
function bodies(id: number): { dark: ChromeBody; light: ChromeBody } {
  return LIGHT_CHROME_FACTIONS.find((r) => r.id === String(id))
    ?? LIGHT_CHROME_FACTIONS.find((r) => r.id === String(FALLBACK_FACTION))!;
}

/** The picture of one identity, as the SVG a tile holds. The view box is
 *  120 × 50, as the chart pictures are. */
export function identityPicture(identity: Identity, faction = FALLBACK_FACTION): string {
  const b = bodies(faction)[identity];
  return '<svg viewBox="0 0 120 50" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">'
    + `<rect width="120" height="50" rx="3" fill="${b.deep}"/>`
    + `<path d="M14 8l8 4.5v9l-8 4.5-8-4.5v-9z" fill="none" stroke="${b.faint}" stroke-width="0.8"/>`
    + `<rect x="30" y="8" width="82" height="36" rx="2" fill="${b.panel}" stroke="${b.faint}" stroke-width="0.6"/>`
    + `<rect x="36" y="14" width="34" height="4" rx="1" fill="${b.text}"/>`
    + `<rect x="36" y="23" width="56" height="2.5" rx="1" fill="${b.dim}"/>`
    + `<rect x="36" y="29" width="44" height="2.5" rx="1" fill="${b.dim}"/>`
    + `<rect x="84" y="34" width="22" height="6" rx="1.5" fill="${b.accent}"/>`
    + '</svg>';
}

/**
 * The identity choice: one tile for DARK and one for LIGHT. `chosen` is
 * the lit tile (the pick that waits, else the identity in force), `now`
 * the identity in force, which wears the NOW tag while a pick waits.
 * `confirm` is the strip of RELOAD NOW under the tiles.
 */
export function identityPick(chosen: Identity, now: Identity, act: string,
  words: Readonly<Record<Identity, string>>, o: { label?: string; confirm?: Confirm; faction?: number } = {}): string {
  const tiles = IDENTITIES.map((id) => ({
    id, label: words[id], picture: identityPicture(id, o.faction),
    mark: o.confirm && id === now ? CS.now : undefined,
  }));
  // One column: the strip stands under the tiles, never beside them in
  // the flex row of the control slot.
  return '<span class="ch-tconf">'
    + previewTiles(tiles, chosen, act, { label: o.label, cols: IDENTITIES.length })
    + (o.confirm ? confirmStrip(o.confirm, { free: true }) : '') + '</span>';
}

